import * as React from "react";

const ContentContext = React.createContext({
  content: {},
  loading: true,
  clientSlug: "",
});

export function ContentProvider({ clientSlug, apiUrl, children }) {
  const [content, setContent] = React.useState({});
  const [loading, setLoading] = React.useState(Boolean(apiUrl));

  React.useEffect(() => {
    if (!apiUrl || !clientSlug) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const endpoint = `${apiUrl.replace(/\/$/, "")}/v1/content/${clientSlug}`;

    fetch(endpoint)
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (cancelled) return;
        if (body?.ok && body.content) {
          setContent(body.content);
        }
      })
      .catch(() => {
        /* fall back to baked-in defaults in components */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiUrl, clientSlug]);

  return (
    <ContentContext.Provider value={{ content, loading, clientSlug }}>
      {children}
    </ContentContext.Provider>
  );
}

/** Read a merged content value; falls back when API is unavailable. */
export function useContent(path, fallback = "") {
  const { content } = React.useContext(ContentContext);
  const value = content?.[path];
  if (typeof value === "string" && value.trim()) {
    return value;
  }
  return fallback;
}

export function useContentContext() {
  return React.useContext(ContentContext);
}
